import type { JSX } from "react";

export type WorkspaceMode = "video" | "motion";

// Motion Design is parked for v2. This flag is the single switch that brings it
// back in one move: with it off, Toolbar.tsx hides the whole workspace switch
// (a one-item tab bar is noise) and App.tsx closes the motion route guard.
// Flip it to `true` to restore both. This component still knows both modes, so
// nothing here is deleted, only unmounted by its callers while the flag is off.
export const MOTION_DESIGN_ENABLED = false;

const MODES: Array<{
  id: WorkspaceMode;
  label: string;
}> = [
  { id: "video", label: "Video Editor" },
  { id: "motion", label: "Motion Design" },
];

export function WorkspaceModeTabs({
  activeMode,
  onSelectMode,
  ariaLabel = "Editor workspaces",
  className = "",
  accessibleLabels,
}: {
  activeMode: WorkspaceMode;
  onSelectMode: (mode: WorkspaceMode) => void;
  ariaLabel?: string;
  className?: string;
  accessibleLabels?: Partial<Record<WorkspaceMode, string>>;
}): JSX.Element {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`inline-flex items-center gap-0 rounded-lg bg-bg-3 p-[3px] ${className}`}
    >
      {MODES.map((mode) => {
        const isActive = activeMode === mode.id;
        return (
          <button
            key={mode.id}
            type="button"
            role="tab"
            aria-label={accessibleLabels?.[mode.id]}
            aria-selected={isActive}
            onClick={() => onSelectMode(mode.id)}
            className={`rounded-[7px] px-4 py-[7px] text-[13px] transition-colors ${
              isActive
                ? "bg-bg-1 text-fg font-normal border border-border"
                : "bg-transparent text-fg-3 font-medium hover:text-fg-2"
            }`}
          >
            {mode.label}
          </button>
        );
      })}
    </div>
  );
}
